<template>
  <div class="mod">
    <div id="fdd-plot" class="peak-wrapper noselect">
      <!-- Header -->
      <div class="over-info">
        <h2>Modal Frequencies Clustering</h2>
        <div class="select-container">
          <ModalCalendar
            v-if="showCalendar && tempDateRange[0] && tempDateRange[1]"
            :datarange="[tempDateRange[0], tempDateRange[1]]"
            @close="showCalendar = false"
            @update="updateDate"
            @range-selected="rangeSelected"
            :maxDate="isLambro ? new Date('2021-04-23') : new Date()"
            class="modal-calendar"
            id="calendar"
          />
          <div @click="showCalendar = !showCalendar" class="info-group">
            <p class="info-btn">{{ $t("message.dateRange") }}:</p>
            <h2 class="info-btn">{{ this.formattedDate[0] + " - " + this.formattedDate[1] }}</h2>
            <i class="info-btn" v-bind:class="[showCalendar ? 'up' : 'down']"></i>
          </div>
        </div>
      </div>
      <div id="graph-wrapper">
        <!-- Wrapper Info in Overlay -->
        <div class="infoi" v-show="this.loading || !this.freqsFromApi || !this.freqsFromApi.Modalfreqs">
          <h2 v-if="(!this.freqsFromApi || !this.freqsFromApi.Modalfreqs) && !this.loading">No FDD Data...</h2>
          <h2 v-else>{{ $t("message.loading") }}...</h2>
        </div>
        <!-- Grafico Cluster -->
        <div class="mod-plot">
          <ModalPlot :height="530" :traces="this.modalTraces" xTitle="Frequency (Hz)" yTitle="Frequency (Hz)" @selected="selectFreq" />
        </div>
        <!-- Gradico Dens. Prob. -->
        <div class="dens-plot">
          <p>Selected modal freq. avg: {{ this.freqsFromApi ? this.freqsFromApi.Modalfreqs[selectedClusterIndex].avg.toFixed(4) : "-" }} Hz</p>
          <DensProb class="graph" :values="this.probTraces" xTitle="Freq [Hz]" :yTitle="$t('message.freqApp')" v-if="true" />
        </div>
        <!-- Tabella Frequenze -->
        <div class="info-tab">
          <table style="width: 100%">
            <tr>
              <th></th>
              <th>{{ $t("message.minValue") }}</th>
              <th>{{ $t("message.meanValue") }}</th>
              <th>{{ $t("message.maxValue") }}</th>
              <th>{{ $t("message.stdDeviation") }}</th>
              <th>ΔF(%)</th>
            </tr>
            <tr
              :class="[index === selectedClusterIndex ? 'selected' : '', 'tab-row']"
              v-for="(cluster, index) in this.freqsFromApi ? this.freqsFromApi.Modalfreqs : []"
              :key="index"
              @click="selectFreq(index)"
            >
              <td><span class="table-dot" :style="{ backgroundColor: index < colorsMap.length ? colorsMap[index] : '' }"></span></td>
              <td>{{ cluster.min.toFixed(4) }}</td>
              <td>{{ cluster.avg.toFixed(4) }}</td>
              <td>{{ cluster.max.toFixed(4) }}</td>
              <td>{{ cluster.std_deviation.toFixed(4) }}</td>
              <td>{{ cluster.deltaF.toFixed(4) }}</td>
            </tr>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { mapGetters } from "vuex";
import ModalPlot from "../../graphs/ModalPlot.vue";
import ModalCalendar from "../../ui/ModalCalendar";
import DensProb from "../../graphs/DensityProbHistogram";
import api from "../../../services/api";

export default {
  name: "ModalFreqs",
  props: {
    sensorsType: String,
  },
  components: {
    ModalPlot,
    ModalCalendar,
    DensProb,
  },
  data() {
    return {
      loading: false,
      tempDateRange: [],
      actualDateRange: [],
      showCalendar: false,
      selectedClusterIndex: null,
      binSize: 0.005,
      freqsFromApi: null,
      colorsMap: [],
    };
  },
  computed: {
    ...mapGetters({
      getFDD: "FDD/getFDD",
      getSelectedGroupNumber: "FDD/getSelectedGroupNumber",
      getSelectedStructure: "structure/getSelectedStructure",
      getSensors: "structure/getSensors",

      /* getDateRange: "FDD/getFreqClusterDateRange", */
    }),
    isLambro() {
      return this.getSensors ? this.getSensors.some((sn) => sn.eui === "C0EE400001015144") : false;
    },
    formattedDate: function () {
      let firstDate = this.formatDate(this.actualDateRange[0]);
      let lastDate = this.formatDate(this.actualDateRange[1]);
      return [firstDate, lastDate];
    },
    // Computed Traces freq. modali
    modalTraces: function () {
      let traces = [];
      if (this.freqsFromApi && this.freqsFromApi.Modalfreqs) {
        const sortedFreqs = this.freqsFromApi.Modalfreqs;
        sortedFreqs.sort(function (a, b) {
          return a.avg - b.avg;
        });
        sortedFreqs.map((mf, index) => {
          traces.push({
            x: mf.freqs.map((dt) => dt.toFixed(6)),
            y: mf.freqs.map((dt) => dt.toFixed(6)),
            name: "Cluster " + index,
            marker: { color: this.colorsMap.length === sortedFreqs.length ? this.colorsMap[index] : null },
          });
        });
        if (this.freqsFromApi.Outliersfreqs) {
          traces.push({
            x: this.freqsFromApi.Outliersfreqs,
            y: this.freqsFromApi.Outliersfreqs,
            name: "Outliers",
            marker: { color: "gray" },
          });
        }
      }
      return traces;
    },
    // Computed Traces dens. prob.
    probTraces: function () {
      if (this.selectedClusterIndex != null && this.selectedClusterIndex != undefined) {
        return this.freqsFromApi.Modalfreqs[this.selectedClusterIndex].freqs;
      } else {
        return [];
      }
    },
  },
  methods: {
    selectFreq(clusterIndex) {
      // Controllo che non sia selezionato un outlier
      if (this.freqsFromApi && this.freqsFromApi.Modalfreqs && clusterIndex < this.freqsFromApi.Modalfreqs.length) {
        this.selectedClusterIndex = clusterIndex;
      }
    },
    updateDate() {
      this.showCalendar = false;
      this.actualDateRange = this.tempDateRange;
    },
    rangeSelected(dateRange) {
      this.tempDateRange = [new Date(dateRange[0]), new Date(dateRange[1])];
    },
    formatDate(date) {
      if (date) {
        let day = date.getDate();
        let year = date.getFullYear();
        let month = date.toLocaleString("default", { month: "short" });
        return day + " " + month + " " + year;
      } else {
        return null;
      }
    },
    // API Fetch
    async fetchCluster() {
      if (this.actualDateRange[0] && this.actualDateRange[1] && this.getSelectedStructure && this.getSelectedGroupNumber) {
        this.loading = true;
        this.colorsMap = [];
        const resp = await api.getFreqCluster(
          new Date(this.actualDateRange[0]),
          new Date(this.actualDateRange[1]),
          this.getSelectedStructure._id,
          this.sensorsType,
          this.getSelectedGroupNumber
        );
        if (Object.keys(resp).length > 0) {
          this.freqsFromApi = JSON.parse(JSON.stringify(resp));
          if (this.freqsFromApi && this.freqsFromApi.Modalfreqs) {
            this.selectedClusterIndex = this.freqsFromApi.Modalfreqs.length > 0 ? 0 : null;
            this.colorsMap = this.freqsFromApi.Modalfreqs.map(() => "#" + (0x1000000 + Math.random() * 0xffffff).toString(16).substr(1, 6));
          }
        }
        this.loading = false;
      }
    },
  },
  mounted() {
    const firstDate = new Date();
    firstDate.setMonth(firstDate.getMonth() - 1);
    const lastDate = this.isLambro ? new Date('2021-04-23') : new Date();
    this.tempDateRange = [firstDate, lastDate];
    this.actualDateRange = this.tempDateRange;
  },
  watch: {
    getSelectedStructure: {
      handler: async function (newValue, oldValue) {
        console.log("A");
        if (oldValue) {
          console.log("[FDD Clust] New Structure");
          this.fetchCluster();
        }
      },
    },
    actualDateRange: {
      handler: async function (newValue, oldValue) {
        console.log("B");
        if (oldValue) {
          console.log("[FDD Clust] New Date Range");
          this.fetchCluster();
        }
      },
      deep: true,
    },
    getSelectedGroupNumber: {
      handler: async function (newValue, oldValue) {
        console.log("C");
        if (oldValue) {
          console.log("[FDD Clust] New SelectedNumber");
          this.fetchCluster();
        }
      },
    },
    sensorsType: {
      handler: async function (newValue, oldValue) {
        console.log("D");
        if (oldValue) {
          console.log("[FDD Clust] New SensorsType");
          this.fetchCluster();
        }
      },
    },
  },
};
</script>
<style scoped>
/* Main Wrapper */
.mod {
  min-width: 1000px;
  width: 92%;
  display: grid;
  margin-left: 4%;
  margin-right: 4%;
}
/* ID Evento principale */
#fdd-plot {
  width: 100%;
  min-height: 700px;
}
#graph-wrapper {
  width: 100%;
  min-height: 700px;
  grid-row: 2;
  grid-column-start: 1;
  grid-column-end: 3;
  display: grid;
  grid-template-columns: 6.5fr 3.5fr;
  position: relative;
}

/* Wrapper Evento */
.peak-wrapper {
  position: relative;
  margin-top: 30px;
  background-color: rgb(45, 48, 65);
  -webkit-box-shadow: 0px 9px 37px -20px rgba(0, 0, 0, 0.75);
  -moz-box-shadow: 0px 9px 37px -20px rgba(0, 0, 0, 0.75);
  box-shadow: 0px 9px 37px -20px rgba(0, 0, 0, 0.75);
  border-radius: 10px;
}

.mod-plot {
  grid-row: 2;
  grid-column: 1;
  box-sizing: content-box;
  /* border-right: solid 1px gray; */
}
.dens-plot {
  grid-row: 2;
  grid-column: 2;
  box-sizing: content-box;
}
.dens-plot > p {
  text-align: left;
  margin-left: 20px;
}
.info-tab {
  grid-row: 3;
  grid-column: 1/3;
  padding: 40px;
  margin: 0 30px;
  color: white;
}
table {
  border-collapse: collapse;
  border-radius: 20px;
  border-style: hidden; /* hide standard table (collapsed) border */
  box-shadow: 0 0 0 2px rgb(80, 84, 105); /* this draws the table border  */
}

td {
  border: solid 2px rgb(80, 84, 105);
  border-collapse: collapse;
}
table,
th,
td {
  border-left: none;
  border-right: none;
  overflow: hidden;
}

.table-dot {
  height: 15px;
  width: 15px;
  background-color: unset;
  border-radius: 50%;
  /* display: inline-block; */
  display: block;
  text-align: center;
  margin: 0 auto;
}

/* Overlay Info */
.infoi {
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  border-radius: 10px;
  background-color: black;
  z-index: 9;
  opacity: 0.7;
}
.infoi > h2 {
  margin: auto;
}

/* Info in alto sinistra sul tipo di grafico */
.over-info > h2 {
  font-size: 1em;
  color: white;
  margin-top: 0;
  margin-bottom: 0;
}
.over-info > p {
  font-size: 0.8em;
  margin-top: 0;
  margin-bottom: 0;
}
.over-info {
  grid-row: 1;
  grid-column: 1/3;
  height: fit-content;
  text-align: left;
  position: relative;
  margin-top: 2%;
  padding-bottom: 10px;
  display: grid;
  padding-left: 60px;
  padding-right: 60px;
  border-bottom: solid 1px rgba(255, 255, 255, 0.6);
  grid-template-columns: 100%;
}

.select-container {
  color: white;
}

.info-group {
  cursor: pointer;
  display: flex;
  align-items: baseline;
}

.info-group > p {
  margin-right: 5px;
}

.tab-row {
  cursor: pointer;
}
.tab-row:hover {
  background-color: rgba(150, 153, 161, 0.2);
  z-index: -1;
}
.selected {
  background-color: rgb(31, 34, 46);
}

/* Freccia */
i {
  border: solid white;
  border-width: 0 3px 3px 0;
  display: inline-block;
  padding: 5px;
  position: relative;
  margin-bottom: auto;
  margin-top: auto;
  margin-left: 10px;
  bottom: 2px;
  transition: transform 0.3s cubic-bezier(0.42, 0, 0.1, 0.96);
}
.up {
  transform: rotate(-135deg);
  -webkit-transform: rotate(-135deg);
}

.down {
  transform: rotate(45deg);
  -webkit-transform: rotate(45deg);
}

#calendar {
  position: absolute;
  top: 83px;
  max-width: 770px;
  margin-left: 5%;
  background-color: rgb(45, 48, 65);
}
</style>
